import { getHeadlessCmsBrandName, getServiceBrandName } from 'services/brand'
import { axios, authTokenHeader } from '.'
import { Analytics } from '../analytics'

const {
  REACT_APP_WEBSITE_BRAND,
  REACT_APP_COUNTRYCODE,
  REACT_APP_PRODUCT,
  REACT_APP_ENTRYPOINT,
} = process.env

const DEFAULT_RESOLVE_STATUSES = [200, 201]

export const TOKEN_GENERATION_PATH = '/token-transactionId'
const cmsBrandName = getHeadlessCmsBrandName(REACT_APP_WEBSITE_BRAND)

const nonProtectedPaths = [
  { primary: '/journey', secondary: '/continue' },
  {
    primary: '/content-integration',
    secondary: `/v1/${cmsBrandName}/pl`,
  },
]

/**
 * @param {Object} props - Props.
 * @param {string} props.method - ("POST")
 * @param {string} props.path - ("/tan")
 * @param {string} props.transactionId - ID for path parameter
 * @param {string} props.data - request body for POST, PUT etc.
 * @param {string} props.secondaryPath ("/generate")
 * @param {boolean} props.applyProductPath - add brand, product etc to path (default: true)
 * @param {string} props.resolveStatuses - http statuses for promise resolve (default: [200])
 * @param {string} props.onError - method for error handling
 * @param {boolean} props.customHandleReject - if false, onError is used for Promise.reject (default: false)
 * @param {Object} props.headers - Custom headers.
 * @param {Object} props.params - Query parameters.
 * @param {string} props.paymentTransactionId - Payment ID
 */
const fesRequest = props => {
  const {
    transactionId,
    method,
    data,
    path: primaryPath,
    secondaryPath = '',
    applyProductPath = true,
    resolveStatuses = DEFAULT_RESOLVE_STATUSES,
    onError = handleError,
    customHandleReject = false,
    customOnError,
    headers = {},
    params,
    serviceBrandName = getServiceBrandName(),
  } = props

  const isNotProtectedPath = nonProtectedPaths.some(
    ({ primary, secondary }) =>
      primary === primaryPath && secondary === secondaryPath
  )
  const isCallBeforeTokenSetup = !axios.defaults.headers.common[authTokenHeader]
  const isNotCallForTokenGeneration = primaryPath !== TOKEN_GENERATION_PATH

  if (
    isCallBeforeTokenSetup &&
    isNotCallForTokenGeneration &&
    !isNotProtectedPath
  ) {
    // eslint-disable-next-line no-console
    console.error(
      'No call should be executed before setting up transaction (token and id).'
    )
    return Promise.reject()
  }

  const productPath = applyProductPath
    ? `/${REACT_APP_PRODUCT}/${REACT_APP_COUNTRYCODE}/${serviceBrandName}/${REACT_APP_ENTRYPOINT}`
    : ''
  const transactionPath = transactionId ? `/${transactionId}` : ''

  const url = `${primaryPath}${productPath}${transactionPath}${secondaryPath}`

  return new Promise((resolve, reject) => {
    axios({
      method,
      url,
      data,
      params,
      headers,
      validateStatus: statusCode => resolveStatuses.includes(statusCode),
    })
      .then(
        response => {
          const result =
            response.status === 200 && response.data ? response.data : response
          resolve(result)
        },
        error => {
          if (!customHandleReject) {
            onError({ error, url })
          }
          if (customOnError) {
            customOnError()
          }

          reject(error)
        }
      )
      .catch(error => {
        onError({ error, url })
      })
  })
}

export const handleError = ({ error, url }) => {
  analyticsTrackServiceError(url, error.response?.status, error.message)
}

const analyticsTrackServiceError = (url, errorCode, errorMessage) => {
  const pageNames = {
    '500': 'Error: Internal server error (500)',
    '503': 'Error: Service unavailable (503)',
  }

  Analytics.track({
    pageName: pageNames[`${errorCode}`],
    events: [
      {
        eventInfo: {
          eventType: 'Error',
          eventAction: 'Technical error',
        },
      },
    ],
    errorCode,
    errorMessage,
    errorCausingURL: `${process.env.REACT_APP_API_URL}${url}`,
  })
  Analytics.page()
}

export default fesRequest
